import EmptyState from '@/components/EmptyState';
import useError from '@/hooks/useError';
import { Clock01Icon } from '@/icons';
import { formatDateRange } from '@/utils';
import { Flex, Spin } from 'antd';
import dayjs from 'dayjs';
import { ResponsiveContainer, Treemap } from 'recharts';
import { useGetGraceAbuseIndexQuery } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import CustomRangePicker from '../../../../../../components/CustomRangePicker';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';

/* ------------------ COLORS ------------------ */
const COLORS = [
  '#2a7a8e',
  '#247d5a',
  '#0d348a',
  '#aa7f0e',
  '#436b1d',
  '#8a1a8a',
  '#c94a6a',
  '#f2a65a',
  '#d6b85a',
  '#55cfc0',
];

/* ------------------ CUSTOM CONTENT ------------------ */
const CustomTreemapContent = (props: any) => {
  const { x, y, width, height, name, time, rank, color } = props;

  // Granular size detection
  const isExtraSmall = width < 100 || height < 100;

  return (
    <g>
      <foreignObject x={x + 2} y={y + 2} width={width - 4} height={height - 4}>
        <div
          className={`rounded-lg flex flex-col justify-end w-full h-full relative overflow-hidden transition-all duration-300 hover:brightness-110 ${
            isExtraSmall ? 'p-2' : 'p-4'
          }`}
          style={{ backgroundColor: color }}
        >
          <div className="flex flex-col gap-0.5 z-10 w-full">
            <span
              className={`inline-block w-fit bg-white/95 text-black px-1 py-0.5 rounded font-bold mb-1 ${
                isExtraSmall ? 'text-[8px]' : 'text-[10px]'
              }`}
            >
              {String(rank).padStart(2, '0')}.
            </span>
            <h3
              className={`text-white font-bold leading-tight break-words ${
                width > 180
                  ? 'text-lg'
                  : width > 120
                    ? 'text-sm'
                    : 'text-[11px]'
              }`}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {name}
            </h3>
            <div
              className={`text-white/80 flex ${
                width > 200 ? 'items-center gap-2 text-sm' : 'flex-col'
              } ${isExtraSmall ? 'text-[9px]' : 'text-[10px]'}`}
            >
              <span className="opacity-90 whitespace-nowrap">GTA Count</span>

              <span
                className={`font-bold text-white ${
                  width > 200 ? 'text-lg' : isExtraSmall ? 'text-xs' : 'text-sm'
                }`}
              >
                {time}
              </span>
            </div>
          </div>
        </div>
      </foreignObject>
    </g>
  );
};

/* ------------------ COMPONENT ------------------ */
const GraceTimeAbuseIndex = ({
  department_id,
  office_id,
  filter: allFilters,
  setFilter,
  isFilterChanged,
  resetFilter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
  setFilter: (key: any, value: any) => void;
  isFilterChanged: (key: any) => boolean;
  resetFilter: (key: any) => void;
}) => {
  const filter = allFilters.graceTimeAbuseIndex;

  const {
    data: abuseData,
    isLoading,
    error,
  } = useGetGraceAbuseIndexQuery({
    start_date: filter.start_date || '',
    end_date: filter.end_date || '',
    department_id,
    office_id,
    limit: 10,
  });

  useError(error, 'Failed to fetch grace time abuse index');

  const topAbusers = abuseData?.data?.data?.top_abusers || [];

  const treemapData = topAbusers.map((item, idx) => ({
    name: item.name,
    value: item.grace_abuse_count,
    time: item.grace_abuse_count,
    rank: item.rank,
    color: COLORS[idx % COLORS.length],
  }));

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-6 rounded-xl w-full space-y-6">
      <Flex justify="space-between" align="center">
        <h4 className="text-xl text-white font-medium">
          Grace Time Abuse Index{' '}
          <span className="block text-sm text-theme-primary font-normal mt-1 text-opacity-80">
            {formatDateRange(filter.start_date, filter.end_date)}
          </span>
        </h4>

        <CustomRangePicker
          startDate={filter.start_date}
          endDate={filter.end_date}
          onChange={(start, end) => {
            setFilter('graceTimeAbuseIndex', {
              start_date: start,
              end_date: end,
            });
          }}
          disabledDate={(current) =>
            current > dayjs().subtract(1, 'day').endOf('day')
          }
          isChanged={isFilterChanged('graceTimeAbuseIndex')}
          onReset={() => resetFilter('graceTimeAbuseIndex')}
        />
      </Flex>

      <div className="w-full h-[400px] flex items-center justify-center">
        {isLoading ? (
          <Spin size="large" />
        ) : treemapData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={treemapData}
              dataKey="value"
              aspectRatio={4 / 3}
              stroke="#fff"
              fill="#8884d8"
              content={<CustomTreemapContent />}
              isAnimationActive={false}
            />
          </ResponsiveContainer>
        ) : (
          <EmptyState
            icon={<Clock01Icon size={40} />}
            title="No Abuse Index Data"
            description="No top abuser data found for the selected timeframe."
            className="py-0"
          />
        )}
      </div>
    </div>
  );
};

export default GraceTimeAbuseIndex;
